import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, reactive } from 'vue';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import uniqBy from 'lodash/uniqBy';

import { useMe } from '@composables';
import { modals } from '@modals';
import { License, Product } from '@models';
import { alert } from '@services/alert';
import { useGetter, useCommit, Shop } from '@store';
import { ensureError } from '@tools/ensure-error';
import { move } from '@utils/array';
import * as currencies from '@utils/currencies';

import DisplayTextDate from '@components/Display/DisplayTextDate.vue';
import DisplayTextCurrency from '@components/Display/DisplayTextCurrency.vue';
import Icon from '@components/Icon.vue';

import { faIdCard } from '@fortawesome/pro-solid-svg-icons/faIdCard';

interface ProductInfo {
  id: string;
  name: string;
  onSale: boolean;
  price: number;
  salePrice: number;
}


declare module 'vue/types/vue' {
  export interface Vue {
    CardActiveLicenses: ComponentWithProps;
  }
}

const __default__ = { name: 'CardActiveLicenses' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const me = useMe();

const offerings = useGetter('shop/existingUserOfferings');
const addItem = useCommit('cart/ADD_ITEM');

const focusedLicenseId = ref<License['id'] | null>(null);

const products = computed(() => {
  const items: ProductInfo[] = [];

  for (const offering of offerings.value ?? []) {
    // NOTE: Price currently assumed to be first `price` in the `prices` array
    // with a currency in USD.
    const price = currencies.getProductPrice(offering.prices);

    // NOTE: Sale price usage is currently disabled due to Stripe pricing
    // migration.
    // const salePrice = offering.salePrice;
    const salePrice = price;

    items.push({
      id: offering.id,
      name: offering.name,
      onSale: offering.onSale,
      price,
      salePrice,
    });
  }

  return items;
});

// const licenses = computed(() => {
//   return (me.licenses ?? []).map((license) => {
//     return reactive({ ...license, show: false });
//   });
// });

function createLicenseListElement(license: License) {
  const id = license.id;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const displayName = license.productName || license.productId;
  const expiration = license.expiration;
  const scenes = license.scenes ?? [];
  const removeDate = license.removeDate;

  const highlighted = computed(() => id === focusedLicenseId.value);

  return reactive({
    id,
    displayName,
    expiration,
    scenes,
    removeDate,
    highlighted,
  });
}

const licenses = computed(() => {
  return (me.licenses ?? []).map(createLicenseListElement);
});

function createScenarioListElement(scenario: Product.SceneInfo) {
  const id = scenario.id;
  const displayName = scenario.name;

  const highlighted = computed(() => {
    if (!focusedLicenseId.value) return false;

    const license = licenses.value.find((l) => l.id === focusedLicenseId.value);

    return !!find(license?.scenes ?? [], { id });
  });

  return reactive({
    id,
    displayName,
    highlighted,
  });
}

const scenarios = computed(() => {
  return uniqBy(
    licenses.value.flatMap(({ scenes }) =>
      scenes.map(createScenarioListElement),
    ),
    'id',
  );
});

const unownedProducts = computed(() => {
  if (!products.value?.length) return [];

  let items = [...products.value];

  const cheapItemIndex = findIndex(items, ({ price }) => price <= 20);

  if (cheapItemIndex >= 0) {
    // Shift cheaper product to front.
    items = move(items, cheapItemIndex, 0);
  }

  return items.slice(0, 2);
});

function setFocusedLicenseId(licenseId?: License['id']) {
  focusedLicenseId.value = licenseId ?? null;
}

function addToCart(productId: Shop.Item['id']) {
  const item = find(offerings.value ?? [], { id: productId });

  if (!item) {
    throw new Error(
      `[addToCart] could not find cart item with product ID "${productId}".`,
    );
  }

  let error: ErrorOrNullish = null;

  try {
    addItem({ item });
  } catch (err) {
    error = ensureError(err);
  }

  if (error?.code === 'DUPLICATE_ITEM_SCENES') {
    alert.warning(error.message);
  }
}

function showDetails(productId: string) {
  void modals.shop.productDetails({ productId });
}

return { __sfc: true,me, offerings, addItem, focusedLicenseId, products, createLicenseListElement, licenses, createScenarioListElement, scenarios, unownedProducts, setFocusedLicenseId, addToCart, showDetails, DisplayTextDate, DisplayTextCurrency, Icon, faIdCard }
}

})